import type { ReactNode } from "react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Copy,
  Download,
  ExternalLink,
  ListPlus,
  Loader2,
  PauseCircle,
  Play,
  Plus,
  RefreshCw,
  Save,
  Search,
  Trash2,
} from "lucide-react";
import { AdminGate } from "@/components/admin/AdminGate";
import { AdminLayout } from "@/components/admin/AdminLayout";
import {
  AdminActionMenu,
  AdminMenuItem,
} from "@/components/admin/AdminPrimitives";
import { Button, buttonVariants } from "@/components/ui/button";
import { api } from "@/lib/api";
import {
  amazonTakedownCapability,
  amazonTakedownCapabilityLabel,
} from "@/lib/amazonAccess";

type CollaboratorStatus = "none" | "invite_sent" | "verified";
type StorefrontDiscoveryStatus = "none" | "discovering" | "done" | "failed";
type MonitoringCadence = "default" | "daily" | "weekly";

type CustomerBrand = {
  id: string;
  name: string;
  brandRegistry: boolean;
  brandRegistryAnsweredAt: string | null;
  collaboratorStatus: CollaboratorStatus;
  inviteSentAt: string | null;
  verifiedAt: string | null;
  storefrontUrl: string | null;
  storefrontDiscoveryStatus: StorefrontDiscoveryStatus;
  storefrontDiscoveryAt: string | null;
  createdAt: string;
  asinCount: number;
};

type Customer = {
  id: string;
  email: string;
  organizationName: string;
  brandRegistry: boolean;
  collaboratorStatus: CollaboratorStatus;
  inviteSentAt: string | null;
  verifiedAt: string | null;
  createdAt: string;
  brands: CustomerBrand[];
};

type AdminBrandProduct = {
  id: string;
  asin: string;
  productTitle: string | null;
  catalogSource: string;
  monitoringEnabled: boolean;
  monitoringCadence: MonitoringCadence;
  effectiveCadence: "daily" | "weekly";
  addedAt: string;
  lastScrapedAt: string | null;
  nextRunAt: string | null;
  isDue: boolean;
};

type AdminBrandProductList = {
  products: AdminBrandProduct[];
  page: number;
  pageSize: number;
  total: number;
  pageCount: number;
};

const discoveryLabels: Record<StorefrontDiscoveryStatus, string> = {
  none: "Not synced",
  discovering: "Syncing",
  done: "Synced",
  failed: "Failed",
};

function Pill({
  children,
  tone,
}: {
  children: ReactNode;
  tone: "neutral" | "blue" | "green" | "red";
}) {
  const cls = {
    neutral: "bg-muted text-muted-foreground ring-border",
    blue: "bg-sky-50 text-sky-700 ring-sky-200",
    green: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    red: "bg-red-50 text-red-700 ring-red-200",
  }[tone];
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {children}
    </span>
  );
}

function discoveryTone(status: StorefrontDiscoveryStatus) {
  if (status === "done") return "green";
  if (status === "discovering") return "blue";
  if (status === "failed") return "red";
  return "neutral";
}

function formatDateTime(value: string | null) {
  return value ? new Date(value).toLocaleString() : "—";
}

function CustomerEditor({
  customer,
  onChange,
}: {
  customer: Customer;
  onChange: () => void;
}) {
  const [email, setEmail] = useState(customer.email);
  const [inviteEmail, setInviteEmail] = useState(customer.email);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setEmail(customer.email);
    setInviteEmail(customer.email);
  }, [customer.email]);

  async function saveEmail() {
    setError(null);
    setSaving(true);
    const res = await api(`/admin/customers/${customer.id}`, {
      method: "PATCH",
      body: JSON.stringify({ email: email.trim() }),
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error ?? "Could not update customer.");
      return;
    }
    onChange();
  }

  async function sendInvite() {
    setError(null);
    setInviteUrl("");
    setSending(true);
    const res = await api<{ inviteUrl: string }>(
      `/admin/customers/${customer.id}/resend-invite`,
      {
        method: "POST",
        body: JSON.stringify({
          email: inviteEmail.trim(),
          updateCustomerEmail: inviteEmail.trim() !== customer.email,
        }),
      }
    );
    setSending(false);
    if (!res.ok) {
      setError(res.error ?? "Could not send invite.");
      return;
    }
    setInviteUrl(res.data?.inviteUrl ?? "");
    onChange();
  }

  async function copyInvite() {
    if (!inviteUrl) return;
    await navigator.clipboard.writeText(inviteUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }

  return (
    <section className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Organization account</h2>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-2">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Account email
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <Button type="button" size="sm" onClick={saveEmail} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Save
            </Button>
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Reputely portal invite
          </label>
          <div className="flex gap-2">
            <input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="min-w-0 flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
            />
            <Button type="button" size="sm" onClick={sendInvite} disabled={sending}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Send
            </Button>
          </div>
        </div>
      </div>

      {inviteUrl && (
        <div className="mt-4 rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
          <div className="flex items-center gap-2 font-medium">
            <CheckCircle2 className="h-4 w-4" /> Invite sent
          </div>
          <div className="mt-2 flex gap-2">
            <input
              readOnly
              value={inviteUrl}
              onClick={(e) => e.currentTarget.select()}
              className="min-w-0 flex-1 rounded-md border border-emerald-200 bg-white px-3 py-2 font-mono text-xs"
            />
            <Button type="button" size="sm" variant="outline" onClick={copyInvite}>
              <Copy className="h-4 w-4" />
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" /> {error}
        </p>
      )}
    </section>
  );
}

function BrandProducts({
  brand,
  onBrandChange,
  refreshKey,
}: {
  brand: CustomerBrand;
  onBrandChange: () => void;
  refreshKey: number;
}) {
  const [products, setProducts] = useState<AdminBrandProduct[] | null>(null);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    total: 0,
    pageCount: 1,
  });
  const [loading, setLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);
  const [selectedProductIds, setSelectedProductIds] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState<"pause" | "resume" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addAsin, setAddAsin] = useState("");
  const [addTitle, setAddTitle] = useState("");
  const [addCadence, setAddCadence] = useState<MonitoringCadence>("daily");
  const [addRunNow, setAddRunNow] = useState(true);
  const [adding, setAdding] = useState(false);

  const load = useCallback((nextPage = page) => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      page: String(nextPage),
      pageSize: String(pagination.pageSize),
    });
    const trimmedQuery = query.trim();
    if (trimmedQuery) params.set("q", trimmedQuery);
    api<AdminBrandProductList>(`/admin/brands/${brand.id}/products?${params}`).then(
      (res) => {
        setLoading(false);
        if (!res.ok || !res.data) {
          setError(res.error ?? "Could not load products.");
          setProducts([]);
          setPagination((current) => ({ ...current, total: 0, pageCount: 1 }));
          return;
        }
        setProducts(res.data.products);
        setSelectedProductIds([]);
        setPagination({
          page: res.data.page,
          pageSize: res.data.pageSize,
          total: res.data.total,
          pageCount: res.data.pageCount,
        });
      }
    );
  }, [brand.id, page, pagination.pageSize, query]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  async function updateMonitoring(
    product: AdminBrandProduct,
    payload: Partial<{
      monitoringEnabled: boolean;
      monitoringCadence: MonitoringCadence;
    }>
  ) {
    setActionId(product.id);
    setError(null);
    setMessage(null);
    const res = await api<{ product: AdminBrandProduct }>(
      `/admin/products/${product.id}/monitoring`,
      {
        method: "PATCH",
        body: JSON.stringify(payload),
      }
    );
    setActionId(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not update monitoring.");
      return;
    }
    setProducts((current) =>
      current
        ? current.map((item) =>
            item.id === product.id ? res.data!.product : item
          )
        : current
    );
    setMessage(`Updated ${product.asin}.`);
    onBrandChange();
  }

  function toggleProductSelected(productId: string) {
    setSelectedProductIds((current) =>
      current.includes(productId)
        ? current.filter((id) => id !== productId)
        : [...current, productId]
    );
  }

  function toggleVisibleProducts(checked: boolean) {
    setSelectedProductIds(checked && products ? products.map((product) => product.id) : []);
  }

  async function bulkUpdateMonitoring(monitoringEnabled: boolean) {
    if (selectedProductIds.length === 0) return;
    const label = monitoringEnabled ? "resume" : "pause";
    if (
      !window.confirm(
        `${monitoringEnabled ? "Resume" : "Pause"} monitoring for ${
          selectedProductIds.length
        } selected product${selectedProductIds.length === 1 ? "" : "s"}?`
      )
    ) {
      return;
    }

    setBulkAction(label);
    setError(null);
    setMessage(null);
    const res = await api<{
      updatedCount: number;
      products: AdminBrandProduct[];
    }>(`/admin/brands/${brand.id}/products/monitoring`, {
      method: "PATCH",
      body: JSON.stringify({
        productIds: selectedProductIds,
        monitoringEnabled,
      }),
    });
    setBulkAction(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not update selected products.");
      return;
    }
    setProducts((current) =>
      current
        ? current.map((item) =>
            res.data!.products.find((product) => product.id === item.id) ?? item
          )
        : current
    );
    setSelectedProductIds([]);
    setMessage(
      `${monitoringEnabled ? "Resumed" : "Paused"} monitoring for ${
        res.data.updatedCount
      } product${res.data.updatedCount === 1 ? "" : "s"}.`
    );
    onBrandChange();
  }

  async function runNow(product: AdminBrandProduct) {
    setActionId(product.id);
    setError(null);
    setMessage(null);
    const res = await api<{ auditRequestId: string; path: string }>(
      `/admin/products/${product.id}/run-now`,
      {
        method: "POST",
        body: JSON.stringify({}),
      }
    );
    setActionId(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not start monitoring run.");
      return;
    }
    setMessage(
      `Started ${product.asin} via ${res.data.path}. Audit ${res.data.auditRequestId}.`
    );
    load();
  }

  async function addProduct() {
    setAdding(true);
    setError(null);
    setMessage(null);
    const res = await api<{
      created: boolean;
      product: AdminBrandProduct;
      auditRequestId: string | null;
      path: string | null;
    }>(`/admin/brands/${brand.id}/products`, {
      method: "POST",
      body: JSON.stringify({
        asin: addAsin.trim(),
        productTitle: addTitle.trim() || undefined,
        monitoringEnabled: true,
        monitoringCadence: addCadence,
        runNow: addRunNow,
      }),
    });
    setAdding(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not add product.");
      return;
    }
    setAddAsin("");
    setAddTitle("");
    setAddCadence("daily");
    const runText = res.data.auditRequestId
      ? ` Started ${res.data.path}. Audit ${res.data.auditRequestId}.`
      : "";
    setMessage(
      `${res.data.created ? "Added" : "Updated"} ${res.data.product.asin}.${runText}`
    );
    setPage(1);
    load(1);
    onBrandChange();
  }

  return (
    <div className="mt-5 border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold">Products</h4>
          <p className="text-xs text-muted-foreground">
            Search the synced catalog first. Add an ASIN only when it is missing.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => load()} disabled={loading}>
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Refresh
          </Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setAddOpen((v) => !v)}>
            <Plus className="h-4 w-4" />
            Add product
          </Button>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2">
        <Search className="h-4 w-4 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setPage(1);
          }}
          placeholder="Search ASIN or product title"
          className="min-w-0 flex-1 bg-transparent text-sm outline-none"
        />
      </div>

      {addOpen && (
        <div className="mt-3 grid gap-3 rounded-lg border border-border bg-muted/30 p-3 md:grid-cols-[minmax(140px,0.8fr)_minmax(180px,1.2fr)_140px_auto_auto]">
          <input
            value={addAsin}
            onChange={(e) => setAddAsin(e.target.value)}
            placeholder="ASIN or Amazon URL"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <input
            value={addTitle}
            onChange={(e) => setAddTitle(e.target.value)}
            placeholder="Product title"
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
          <select
            value={addCadence}
            onChange={(e) => setAddCadence(e.target.value as MonitoringCadence)}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm"
          >
            <option value="default">Default</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
          </select>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={addRunNow}
              onChange={(e) => setAddRunNow(e.target.checked)}
            />
            Run now
          </label>
          <Button type="button" size="sm" onClick={addProduct} disabled={adding || !addAsin.trim()}>
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add
          </Button>
        </div>
      )}

      {message && (
        <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700" role="status">
          <CheckCircle2 className="h-4 w-4" /> {message}
        </p>
      )}
      {error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" /> {error}
        </p>
      )}

      {loading && !products && (
        <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading products…
        </div>
      )}

      {products && products.length === 0 && (
        <div className="mt-4 rounded-lg border border-border bg-background p-5 text-sm text-muted-foreground">
          {query.trim() ? "No products match this search." : "No products yet."}
        </div>
      )}

      {products && products.length > 0 && (
        <div className="mt-4 rounded-lg border border-border">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-3 py-2 text-xs text-muted-foreground">
            <span>
              Showing {(pagination.page - 1) * pagination.pageSize + 1}-
              {Math.min(pagination.page * pagination.pageSize, pagination.total)} of{" "}
              {pagination.total} products
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={pagination.page <= 1 || loading}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <ChevronLeft className="h-4 w-4" />
                Prev
              </Button>
              <span>
                Page {pagination.page} of {pagination.pageCount}
              </span>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={pagination.page >= pagination.pageCount || loading}
                onClick={() =>
                  setPage((current) => Math.min(pagination.pageCount, current + 1))
                }
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {selectedProductIds.length > 0 && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/40 px-3 py-2 text-sm">
              <span className="font-medium text-foreground">
                {selectedProductIds.length} selected
              </span>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => bulkUpdateMonitoring(false)}
                  disabled={bulkAction !== null}
                >
                  {bulkAction === "pause" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PauseCircle className="h-4 w-4" />
                  )}
                  Pause selected
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => bulkUpdateMonitoring(true)}
                  disabled={bulkAction !== null}
                >
                  {bulkAction === "resume" ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                  Resume selected
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => setSelectedProductIds([])}
                  disabled={bulkAction !== null}
                >
                  Clear
                </Button>
              </div>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/70 text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="w-10 px-3 py-2 text-left">
                    <input
                      type="checkbox"
                      aria-label="Select visible products"
                      checked={
                        products.length > 0 &&
                        products.every((product) =>
                          selectedProductIds.includes(product.id)
                        )
                      }
                      onChange={(e) => toggleVisibleProducts(e.target.checked)}
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Cadence</th>
                  <th className="px-3 py-2 text-left">Last run</th>
                  <th className="px-3 py-2 text-left">Next run</th>
                  <th className="px-3 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {products.map((product) => {
                  const busy = actionId === product.id;
                  return (
                    <tr key={product.id}>
                      <td className="px-3 py-3 align-top">
                        <input
                          type="checkbox"
                          aria-label={`Select ${product.asin}`}
                          checked={selectedProductIds.includes(product.id)}
                          onChange={() => toggleProductSelected(product.id)}
                        />
                      </td>
                      <td className="max-w-[320px] px-3 py-3">
                        <Link
                          to={`/admin/products/${product.id}/reviews`}
                          className="group block min-w-0 rounded-md underline-offset-4 hover:underline"
                        >
                          <span className="font-mono text-xs text-primary">
                            {product.asin}
                          </span>
                          {product.productTitle && (
                            <span className="mt-0.5 block truncate text-xs text-muted-foreground group-hover:text-foreground">
                              {product.productTitle}
                            </span>
                          )}
                        </Link>
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {product.catalogSource}
                      </td>
                      <td className="px-3 py-3">
                        <Pill tone={product.monitoringEnabled ? "green" : "neutral"}>
                          {product.monitoringEnabled ? "Monitored" : "Paused"}
                        </Pill>
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={product.monitoringCadence}
                          onChange={(e) =>
                            updateMonitoring(product, {
                              monitoringCadence: e.target.value as MonitoringCadence,
                            })
                          }
                          disabled={busy}
                          className="rounded-md border border-input bg-background px-2 py-1 text-xs"
                        >
                          <option value="default">
                            Default ({product.effectiveCadence})
                          </option>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {formatDateTime(product.lastScrapedAt)}
                      </td>
                      <td className="px-3 py-3 text-xs text-muted-foreground">
                        {product.monitoringEnabled ? formatDateTime(product.nextRunAt) : "—"}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex justify-end gap-2">
                          <Link
                            to={`/admin/products/${product.id}/reviews`}
                            className={buttonVariants({ size: "sm", variant: "outline" })}
                          >
                            Reviews
                          </Link>
                          <Link
                            to={`/admin/products/${product.id}`}
                            className={buttonVariants({ size: "sm", variant: "outline" })}
                          >
                            <ExternalLink className="h-4 w-4" />
                            Details
                          </Link>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() => runNow(product)}
                            disabled={busy}
                          >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                            Run
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateMonitoring(product, {
                                monitoringEnabled: !product.monitoringEnabled,
                              })
                            }
                            disabled={busy}
                          >
                            {product.monitoringEnabled ? (
                              <PauseCircle className="h-4 w-4" />
                            ) : (
                              <CheckCircle2 className="h-4 w-4" />
                            )}
                            {product.monitoringEnabled ? "Pause" : "Resume"}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function BrandRow({
  brand,
  onChange,
}: {
  brand: CustomerBrand;
  onChange: () => void;
}) {
  const takedownCapability = amazonTakedownCapability(brand);
  const [name, setName] = useState(brand.name);
  const [storefrontUrl, setStorefrontUrl] = useState(brand.storefrontUrl ?? "");
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [reviewSyncing, setReviewSyncing] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [monitoringAction, setMonitoringAction] = useState<
    "pause-all" | "resume-all" | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [productsRefreshKey, setProductsRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    setName(brand.name);
    setStorefrontUrl(brand.storefrontUrl ?? "");
  }, [brand.name, brand.storefrontUrl]);

  async function save() {
    setError(null);
    setMessage(null);
    setSaving(true);
    const res = await api(`/admin/brands/${brand.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        name: name.trim(),
        storefrontUrl: storefrontUrl.trim() || null,
      }),
    });
    setSaving(false);
    if (!res.ok) {
      setError(res.error ?? "Could not save brand.");
      return;
    }
    onChange();
  }

  async function updateAccess(
    payload: Partial<{
      brandRegistry: boolean;
      collaboratorStatus: CollaboratorStatus;
    }>
  ) {
    setError(null);
    setMessage(null);
    setAccessSaving(true);
    const res = await api(`/admin/brands/${brand.id}/access`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    setAccessSaving(false);
    if (!res.ok) {
      setError(res.error ?? "Could not update Amazon access.");
      return;
    }
    onChange();
  }

  async function sync() {
    setError(null);
    setMessage(null);
    setSyncing(true);
    const res = await api(`/admin/brands/${brand.id}/sync-product-catalog`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    setSyncing(false);
    if (!res.ok) {
      setError(res.error ?? "Could not start product sync.");
      return;
    }
    onChange();
  }

  async function syncReviews() {
    setError(null);
    setMessage(null);
    setReviewSyncing(true);
    const res = await api<{
      auditRequestId: string;
      asinCount: number;
    }>(`/admin/brands/${brand.id}/sync-reviews`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    setReviewSyncing(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not start review sync.");
      return;
    }
    setMessage(
      `Started review sync for ${res.data.asinCount} ASINs. Audit ${res.data.auditRequestId}.`
    );
    onChange();
  }

  async function queueManualScrapes() {
    setError(null);
    setMessage(null);
    setQueueing(true);
    const res = await api<{
      asinCount: number;
      queuedCount: number;
      skippedCount: number;
    }>(`/admin/brands/${brand.id}/queue-manual-review-scrapes`, {
      method: "POST",
      body: JSON.stringify({}),
    });
    setQueueing(false);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not queue manual scrape override.");
      return;
    }
    setMessage(
      `Queued ${res.data.queuedCount} manual scrape jobs. ${res.data.skippedCount} already had active jobs.`
    );
    onChange();
  }

  async function updateBrandMonitoring(monitoringEnabled: boolean) {
    if (
      !window.confirm(
        `${monitoringEnabled ? "Resume" : "Pause"} monitoring for all ${
          brand.asinCount
        } products in ${brand.name}?`
      )
    ) {
      return;
    }

    setError(null);
    setMessage(null);
    setMonitoringAction(monitoringEnabled ? "resume-all" : "pause-all");
    const res = await api<{
      updatedCount: number;
      totalCount: number;
    }>(`/admin/brands/${brand.id}/products/monitoring-all`, {
      method: "PATCH",
      body: JSON.stringify({ monitoringEnabled }),
    });
    setMonitoringAction(null);
    if (!res.ok || !res.data) {
      setError(res.error ?? "Could not update brand monitoring.");
      return;
    }

    const changed = res.data.updatedCount;
    const unchanged = Math.max(0, res.data.totalCount - changed);
    setMessage(
      `${monitoringEnabled ? "Resumed" : "Paused"} monitoring for ${changed} product${
        changed === 1 ? "" : "s"
      }. ${unchanged} already ${monitoringEnabled ? "monitored" : "paused"}.`
    );
    setProductsRefreshKey((current) => current + 1);
    onChange();
  }

  async function remove() {
    if (!window.confirm(`Delete ${brand.name}? This removes monitored ASINs for the brand.`)) {
      return;
    }
    setError(null);
    setMessage(null);
    setDeleting(true);
    const res = await api(`/admin/brands/${brand.id}`, { method: "DELETE" });
    setDeleting(false);
    if (!res.ok) {
      setError(res.error ?? "Could not delete brand.");
      return;
    }
    onChange();
  }

  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-semibold">{brand.name}</h3>
            <Pill tone={discoveryTone(brand.storefrontDiscoveryStatus)}>
              {discoveryLabels[brand.storefrontDiscoveryStatus]}
            </Pill>
            <Pill
              tone={
                takedownCapability === "brand_registry_ready"
                  ? "green"
                  : takedownCapability === "brand_registry_access_pending"
                    ? "blue"
                    : "neutral"
              }
            >
              {amazonTakedownCapabilityLabel(takedownCapability)}
            </Pill>
            <Pill tone={brand.brandRegistry ? "green" : "neutral"}>
              Registry:{" "}
              {brand.brandRegistry
                ? "Yes"
                : brand.brandRegistryAnsweredAt
                  ? "No"
                  : "Unknown"}
            </Pill>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">
            {brand.asinCount} ASINs · Added {new Date(brand.createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            aria-expanded={productsOpen}
            onClick={() => setProductsOpen((open) => !open)}
          >
            <ListPlus className="h-4 w-4" />
            {productsOpen ? "Hide products" : `Products (${brand.asinCount})`}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={syncReviews}
            disabled={reviewSyncing || brand.asinCount === 0}
            title="Start authenticated Browserbase review scraping for this brand's monitored ASINs."
          >
            {reviewSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Sync reviews
          </Button>
          <AdminActionMenu>
            <AdminMenuItem
              onClick={sync}
              disabled={
                syncing ||
                !brand.storefrontUrl ||
                brand.storefrontDiscoveryStatus === "discovering"
              }
            >
              {syncing || brand.storefrontDiscoveryStatus === "discovering" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Sync products
            </AdminMenuItem>
            <AdminMenuItem
              onClick={queueManualScrapes}
              disabled={queueing || brand.asinCount === 0}
              title="Create manual scrape jobs for this brand's products."
            >
              {queueing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ListPlus className="h-4 w-4" />
              )}
              Manual scrape override
            </AdminMenuItem>
            <AdminMenuItem
              onClick={() => updateBrandMonitoring(false)}
              disabled={monitoringAction !== null || brand.asinCount === 0}
              title="Pause scheduled monitoring for every product in this brand."
            >
              {monitoringAction === "pause-all" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <PauseCircle className="h-4 w-4" />
              )}
              Pause all products
            </AdminMenuItem>
            <AdminMenuItem
              onClick={() => updateBrandMonitoring(true)}
              disabled={monitoringAction !== null || brand.asinCount === 0}
              title="Resume scheduled monitoring for every product in this brand."
            >
              {monitoringAction === "resume-all" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4" />
              )}
              Resume all products
            </AdminMenuItem>
            <a
              href={`/api/admin/brands/${brand.id}/flagged-reviews.csv`}
              className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm hover:bg-muted"
            >
              <Download className="h-4 w-4" />
              Export flagged reviews
            </a>
            <AdminMenuItem
              onClick={remove}
              disabled={deleting}
              className="text-destructive hover:text-destructive"
            >
              {deleting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              Delete brand
            </AdminMenuItem>
          </AdminActionMenu>
        </div>
      </div>

      <div className="mt-4 rounded-lg border border-border bg-muted/20 p-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">Amazon capabilities</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              Monitoring is independent of Brand Registry. Managed takedowns require verified Registered Agent access.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => updateAccess({ brandRegistry: true })}
              disabled={accessSaving}
            >
              Registry: Yes
            </Button>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => updateAccess({ brandRegistry: false })}
              disabled={accessSaving}
            >
              Registry: No
            </Button>
            <Button
              type="button"
              size="sm"
              variant={brand.collaboratorStatus === "none" ? "default" : "outline"}
              onClick={() => updateAccess({ collaboratorStatus: "none" })}
              disabled={accessSaving || !brand.brandRegistry}
            >
              Not started
            </Button>
            <Button
              type="button"
              size="sm"
              variant={brand.collaboratorStatus === "invite_sent" ? "default" : "outline"}
              onClick={() => updateAccess({ collaboratorStatus: "invite_sent" })}
              disabled={accessSaving || !brand.brandRegistry}
            >
              Invite sent
            </Button>
            <Button
              type="button"
              size="sm"
              variant={brand.collaboratorStatus === "verified" ? "default" : "outline"}
              onClick={() => updateAccess({ collaboratorStatus: "verified" })}
              disabled={accessSaving || !brand.brandRegistry}
            >
              {accessSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Verified
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-4 grid gap-3 md:grid-cols-[minmax(180px,0.7fr)_minmax(240px,1.3fr)_auto]">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Brand name
          </label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Storefront URL
          </label>
          <input
            value={storefrontUrl}
            onChange={(e) => setStorefrontUrl(e.target.value)}
            placeholder="https://www.amazon.com/stores/..."
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />
        </div>
        <div className="flex items-end">
          <Button type="button" size="sm" onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </div>
      </div>

      {brand.storefrontUrl && (
        <a
          href={brand.storefrontUrl}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-block text-xs text-muted-foreground hover:text-foreground hover:underline"
        >
          Open storefront
        </a>
      )}
      {message && (
        <p className="mt-3 flex items-center gap-2 text-sm text-emerald-700" role="status">
          <CheckCircle2 className="h-4 w-4" /> {message}
        </p>
      )}
      {error && (
        <p className="mt-3 flex items-center gap-2 text-sm text-destructive" role="alert">
          <AlertTriangle className="h-4 w-4" /> {error}
        </p>
      )}
      {productsOpen && (
        <BrandProducts
          brand={brand}
          onBrandChange={onChange}
          refreshKey={productsRefreshKey}
        />
      )}
    </div>
  );
}

function Inner() {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "account" ? "account" : "brands";
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const reload = useCallback(() => setReloadKey((n) => n + 1), []);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setError(null);
    api<{ customer: Customer }>(`/admin/customers/${id}`).then((res) => {
      if (cancelled) return;
      if (!res.ok || !res.data) {
        setError(res.error ?? "Could not load customer.");
        setCustomer(null);
        return;
      }
      setCustomer(res.data.customer);
    });
    return () => {
      cancelled = true;
    };
  }, [id, reloadKey]);

  return (
    <div className="space-y-6">
      <div>
        <Link to="/admin/customers" className="text-sm text-muted-foreground hover:text-foreground">
          ← Customers
        </Link>
        <div className="mt-3">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Organization
          </div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {customer?.organizationName ?? "Customer"}
          </h1>
          {customer && (
            <p className="mt-1 text-sm text-muted-foreground">
              Account email: {customer.email}
            </p>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}
      {!customer && !error && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      )}

      {customer && (
        <>
          <div className="flex flex-wrap gap-2 border-b border-border">
            {[
              { key: "brands", label: `Brands & products (${customer.brands.length})` },
              { key: "account", label: "Account" },
            ].map((item) => (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  if (item.key === "brands") next.delete("tab");
                  else next.set("tab", item.key);
                  setSearchParams(next);
                }}
                className={`border-b-2 px-1 pb-2 text-sm font-medium ${
                  tab === item.key
                    ? "border-foreground text-foreground"
                    : "border-transparent text-muted-foreground hover:text-foreground"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {tab === "account" ? (
            <div className="space-y-4">
              <CustomerEditor customer={customer} onChange={reload} />
              <section className="rounded-xl border border-border bg-card p-5">
                <h2 className="text-sm font-semibold">Brand summary</h2>
                <div className="mt-3 divide-y divide-border">
                  {customer.brands.length === 0 ? (
                    <p className="py-3 text-sm text-muted-foreground">
                      No brands yet.
                    </p>
                  ) : (
                    customer.brands.map((brand) => (
                      <div
                        key={brand.id}
                        className="flex flex-wrap items-center justify-between gap-3 py-3"
                      >
                        <div>
                          <div className="font-medium">{brand.name}</div>
                          <div className="mt-1 text-xs text-muted-foreground">
                            {brand.asinCount} ASINs
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <Pill tone={discoveryTone(brand.storefrontDiscoveryStatus)}>
                            {discoveryLabels[brand.storefrontDiscoveryStatus]}
                          </Pill>
                          <Pill
                            tone={
                              amazonTakedownCapability(brand) ===
                              "brand_registry_ready"
                                ? "green"
                                : amazonTakedownCapability(brand) ===
                                    "brand_registry_access_pending"
                                  ? "blue"
                                  : "neutral"
                            }
                          >
                            {amazonTakedownCapabilityLabel(amazonTakedownCapability(brand))}
                          </Pill>
                          <Pill tone={brand.brandRegistry ? "green" : "neutral"}>
                            Registry:{" "}
                            {brand.brandRegistry
                              ? "Yes"
                              : brand.brandRegistryAnsweredAt
                                ? "No"
                                : "Unknown"}
                          </Pill>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          ) : (
            <section>
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <h2 className="text-lg font-semibold">Managed brands</h2>
                  <p className="text-sm text-muted-foreground">
                    {customer.brands.length} Amazon {customer.brands.length === 1 ? "brand" : "brands"} under this organization.
                  </p>
                </div>
              </div>
              {customer.brands.length === 0 ? (
                <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                  No brands yet.
                </div>
              ) : (
                <div className="space-y-3">
                  {customer.brands.map((brand) => (
                    <BrandRow key={brand.id} brand={brand} onChange={reload} />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

export default function AdminCustomerDetail() {
  return (
    <AdminGate>
      <AdminLayout>
        <Inner />
      </AdminLayout>
    </AdminGate>
  );
}
